import { graphql, useStaticQuery } from "gatsby"

import { Document } from "@contentful/rich-text-types"

import {
  transformBlogPostAuthorFieldsToCurated,
  transformBlogPostCategoryFieldsToCurated,
} from "../modules/formats"

type ContentfulAsset = {
  id: string
  url: string
  width: number
  height: number
  mimeType: string
}

type ContentfulAssetNode = {
  nodes: ContentfulAsset[]
}

type BlogPostAuthorFields = {
  id: string
  title: string
  description: {
    description: string
  }
  image: ContentfulAsset
}

type BlogPostAuthorFieldsCurated = {
  id: string
  title: string
  description: string
  image: ContentfulAsset
}

type BlogPostCategoryFields = {
  id: string
  title: string
  description: {
    description: string
  }
  image: ContentfulAsset
  isShownInMenu: boolean
}

type BlogPostCategoryFieldsCurated = {
  id: string
  title: string
  description: string
  image: ContentfulAsset
  isShownInMenu: boolean
}

type BodyImageReference = {
  contentful_id: string
  mimeType: string
  url: string
  height: number
  width: number
}

type BlogPostFields = {
  id: string
  title: string
  description: {
    description: string
  }
  publishedDate: string
  body: {
    raw: string
    references: BodyImageReference[]
  }
  image: ContentfulAsset
  category: BlogPostCategoryFields
  author: BlogPostAuthorFields
}

type BlogPostFieldsWithBody = BlogPostFields & {
  body: Document
}

type BlogPostNode = {
  nodes: BlogPostFields[]
}

type BlogCategoryNode = {
  nodes: BlogPostCategoryFields[]
}

type BlogAuthorNode = {
  nodes: BlogPostAuthorFields[]
}

type BlogContentfulQuery = {
  post: BlogPostNode
  category: BlogCategoryNode
  author: BlogAuthorNode
}

type PaginationParams = {
  offset?: number
  limit?: number
  categoryId?: string
}

type PaginatedResult<T> = {
  nodes: T[]
  pageInfo: {
    hasNextPage: boolean
    total: number
  }
}

export function useBlogContentfulQuery() {
  const blogContentfulQuery = useStaticQuery<BlogContentfulQuery>(graphql`
    query BlogContentfulQuery {
      post: allContentfulBlogPost(
        sort: { fields: publishedDate, order: DESC }
      ) {
        nodes {
          id: contentfulid
          title
          description {
            description
          }
          publishedDate
          body {
            raw
            references {
              ... on ContentfulAsset {
                contentful_id
                mimeType
                url
                height
                width
              }
            }
          }
          image {
            url
            width
            height
            mimeType
          }
          category {
            id: contentfulid
            title
            description {
              description
            }
            image {
              url
              width
              height
              mimeType
            }
          }
          author {
            id: contentfulid
            title
            description {
              description
            }
            image {
              url
              width
              height
              mimeType
            }
          }
        }
      }
      category: allContentfulBlogCategory {
        nodes {
          id: contentfulid
          title
          description {
            description
          }
          image {
            url
            width
            height
            mimeType
          }
          isShownInMenu
        }
      }
      author: allContentfulBlogAuthor {
        nodes {
          id: contentfulid
          title
          description {
            description
          }
          image {
            url
            width
            height
            mimeType
          }
        }
      }
    }
  `)

  return {
    getBlogPost: (id: string) => {
      const post = blogContentfulQuery.post.nodes.find((node) => node.id === id)
      if (!post) {
        return undefined
      }
      return post
    },

    getBlogCategory: (
      id: string
    ): BlogPostCategoryFieldsCurated | undefined => {
      const category = blogContentfulQuery.category.nodes.find(
        (node) => node.id === id
      )
      if (!category) {
        return undefined
      }
      return transformBlogPostCategoryFieldsToCurated(category)
    },

    getBlogAuthor: (id: string) => {
      const author = blogContentfulQuery.author.nodes.find(
        (node) => node.id === id
      )
      if (!author) {
        return undefined
      }
      return transformBlogPostAuthorFieldsToCurated(author)
    },

    getBlogPosts: ({
      offset = 0,
      limit = 6,
      categoryId,
    }: PaginationParams = {}): PaginatedResult<BlogPostFields> => {
      let allNodes = blogContentfulQuery.post.nodes

      if (categoryId) {
        allNodes = allNodes.filter((node) => node.category.id === categoryId)
      }

      const paginatedNodes = allNodes.slice(offset, offset + limit)

      return {
        nodes: paginatedNodes,
        pageInfo: {
          hasNextPage: offset + limit < allNodes.length,
          total: allNodes.length,
        },
      }
    },

    getBlogCategories: () =>
      blogContentfulQuery.category.nodes.map(
        transformBlogPostCategoryFieldsToCurated
      ),

    getBlogAuthors: () =>
      blogContentfulQuery.author.nodes.map(
        transformBlogPostAuthorFieldsToCurated
      ),

    getBlogPostsByCategory: ({
      categoryId,
      offset = 0,
      limit = 6,
    }: PaginationParams & {
      categoryId: string
    }): PaginatedResult<BlogPostFields> => {
      const allNodes = blogContentfulQuery.post.nodes.filter(
        (node) => node.category.id === categoryId
      )

      const paginatedNodes = allNodes.slice(offset, offset + limit)

      return {
        nodes: paginatedNodes,
        pageInfo: {
          hasNextPage: offset + limit < allNodes.length,
          total: allNodes.length,
        },
      }
    },

    getBlogPostsByAuthor: ({
      authorId,
      offset = 0,
      limit = 6,
    }: PaginationParams & {
      authorId: string
    }): PaginatedResult<BlogPostFields> => {
      const allNodes = blogContentfulQuery.post.nodes.filter(
        (node) => node.author.id === authorId
      )

      const paginatedNodes = allNodes.slice(offset, offset + limit)

      return {
        nodes: paginatedNodes,
        pageInfo: {
          hasNextPage: offset + limit < allNodes.length,
          total: allNodes.length,
        },
      }
    },

    getBlogCategoriesToShow: () => {
      return blogContentfulQuery.category.nodes
        .filter((category) => category.isShownInMenu)
        .sort((a, b) => (a.title < b.title ? -1 : 1))
    },
  }
}

export type {
  ContentfulAsset,
  ContentfulAssetNode,
  BlogPostAuthorFields,
  BlogPostAuthorFieldsCurated,
  BlogPostCategoryFields,
  BlogPostCategoryFieldsCurated,
  BodyImageReference,
  BlogPostFields,
  BlogPostFieldsWithBody,
  BlogPostNode,
  BlogCategoryNode,
  BlogAuthorNode,
  BlogContentfulQuery,
  PaginationParams,
  PaginatedResult,
}
